// TODO: This file was created by bulk-decaffeinate.
// Fix any style issues and re-enable lint.
/*
 * decaffeinate suggestions:
 * DS206: Consider reworking classes to avoid initClass
 * Full docs: https://github.com/decaffeinate/decaffeinate/blob/master/docs/suggestions.md
 */
import Backbone from 'backbone';

import {SequencePrimer} from 'BioJS';
import {NucleotideType} from 'biojs/BioConstants';
import {SearchPrimerCollection} from 'biojs/SequencePrimer';
import {sanitize} from 'biojs/Util';
import {Sequence} from 'components/Sequence';
import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import * as colors from 'lib/Colors';
import {FindPrimersOptions} from 'molbio/find-primers/FindPrimersConstants';
import FindPrimersResultsCollection from 'molbio/find-primers/FindPrimersResultsCollection';
import {getMisprimeDefaultsFromPreferences, MisprimeOptions} from 'molbio/misprime/MisprimeUtils';
import {PolymerTabState, PolymerTabStateAttributes} from 'molbio/tabstates/PolymerTabState';
import {TabStateDefaults} from 'Navigation';
import {assertNever} from 'util/Types';

/**
 * A subset of the FindPrimersTabState attributes that have constant defaults.
 */
interface FindPrimersDefaults {
  /** Title of the FindPrimersTab in the editor */
  title: string;
  /** Whether or not to highlight bases that match the sequence exactly */
  highlightMatches: boolean;
  /** What type of primer search is being done */
  findType: FindPrimersOptions;
  /** A folder API id; where to save new or edited primers */
  selectedSaveLibrary: string | null;
  /** What folder(s) to run the primer search over */
  /** This is terrible but we also store the worklistId in this list when searching by worklist */
  selectedLibraries: Array<string>;
  /** A substring to search over primer names for */
  searchName: string;
  /** Bases to search over primer contents for OR the raw bases for FindPrimersOptions.BASES */
  searchBases: string;
  /** When creating a primer from a search over arbitrary bases, the name under which to save the primer */
  primerName: string;
  /** Whether or not to show the primer search results section */
  showResults: boolean;
  /** When creating a new primer from a search over arbitrary bases, the color the primer should be */
  color: string;
  /** Base pair index at which to start the primer search */
  minBaseIndex: number;
  /** Base pair index at which to end the primer search */
  maxBaseIndex: number;
  /** Display text for how we are sorting the results */
  sortByText: string;
  /** Nucleotide type of primers to search over */
  nucleotideType: NucleotideType;
  tagSchemaId: string | null;
  // Temporary copy of metadata returned when finding primers.
  moreResults: boolean;
  higherTmResults: boolean;
  lowerTmResults: boolean;
  maxResults: number;
}

const TAB_DEFAULTS: FindPrimersDefaults = {
  title: 'Find Binding Sites',
  highlightMatches: true,
  findType: FindPrimersOptions.FOLDERS,
  selectedSaveLibrary: null,
  selectedLibraries: [],
  searchName: '',
  searchBases: '',
  primerName: '',
  showResults: false,
  color: colors.defaultPastelColor,
  minBaseIndex: 0,
  maxBaseIndex: 0,
  sortByText: 'Location',
  nucleotideType: NucleotideType.DNA,
  tagSchemaId: null,
  moreResults: false,
  higherTmResults: false,
  lowerTmResults: false,
  maxResults: 0,
};

/**
 * The actual full set of attributes. Values enumerated here are set via the static insert method.
 */
interface FindPrimersTabStateAttributes
  extends PolymerTabStateAttributes<Sequence>,
    FindPrimersDefaults,
    MisprimeOptions {
  /** A Backbone collection to keep track of search results over a folder */
  results?: FindPrimersResultsCollection;
  /** A Backbone collection to keep track of search-by-name results over all existing primers */
  searchExisting?: SearchPrimerCollection;
  /** A Backbone collection to keep track of search-by-arbitrary-bases binding site results */
  searchExistingBases?: SearchPrimerCollection;
}

export interface FindPrimersTabState extends PolymerTabState<Sequence> {
  get<K extends keyof FindPrimersTabStateAttributes>(key: K): FindPrimersTabStateAttributes[K];
  set<K extends keyof FindPrimersTabStateAttributes>(
    attributeName: K,
    value: FindPrimersTabStateAttributes[K],
    options?: Backbone.ModelSetOptions
  ): FindPrimersTabState;
  set(obj: Partial<FindPrimersTabStateAttributes>): FindPrimersTabState;
}

export class FindPrimersTabState extends PolymerTabState<Sequence> {
  override initialize(): void {
    this.on('change:findType', () => {
      this.set('showResults', false);
      this.removePrimers();
    });
    this.on('change:nucleotideType', (model: this, nucleotideType: NucleotideType): void => {
      const sanitizedBases = sanitize(this.get('searchBases'), nucleotideType);
      this.set('searchBases', sanitizedBases);
    });
    this.on('change:color', (model: this, color: string): void => {
      for (const primer of this.getNewPrimers()) {
        primer.set({color});
      }
    });
  }

  static insert(
    sequence: Sequence,
    data: Partial<FindPrimersTabStateAttributes>,
    primer?: SequencePrimer
  ): void {
    const searchExisting = new SearchPrimerCollection();
    let {searchName, searchBases, nucleotideType} = TAB_DEFAULTS;
    // for finding already existing primers during arbitrary bases search
    const searchExistingBases = new SearchPrimerCollection();
    if (primer != null) {
      searchName = primer.name();
      searchBases = primer.bases();
      nucleotideType = primer.nucleotideType();
      searchExisting.add(primer);
    }

    const tab = new FindPrimersTabState({
      ...data,
      sequence,
      results: new FindPrimersResultsCollection([], {sequence_id: sequence.id}),
      searchExisting,
      searchExistingBases,
      searchName,
      searchBases,
      nucleotideType,
    });
    sequence.fileState().buffers().addTab(tab);
  }

  override defaults(): TabStateDefaults & FindPrimersDefaults & MisprimeOptions {
    // Fetch from preferences here rather than in TAB_DEFAULTS so that the values can update between refreshes
    let selectedLibraries = FolderDefaults.getList(FolderDefaultsKey.FIND_OLIGO);
    if (!selectedLibraries) {
      const defaultOligoFolder = FolderDefaults.get(FolderDefaultsKey.OLIGO);
      if (defaultOligoFolder != null) {
        selectedLibraries = [defaultOligoFolder];
      } else {
        selectedLibraries = [];
      }
    }
    const selectedSaveLibrary = FolderDefaults.get(FolderDefaultsKey.OLIGO) ?? null;
    return {
      ...super.defaults(),
      ...TAB_DEFAULTS,
      ...getMisprimeDefaultsFromPreferences(),
      selectedLibraries,
      selectedSaveLibrary,
    };
  }

  /**
   * Return whether find primer submission should be disabled due to missing or invalid inputs for the search type.
   */
  findPrimersDisabled(): boolean {
    const findType = this.get('findType');
    const misprimingState = this.getMisprimingState();
    if (
      misprimingState.maxTotalMismatches == null ||
      misprimingState.minMeltingTemp == null ||
      misprimingState.maxMeltingTemp == null
    ) {
      return true;
    } else if (findType === FindPrimersOptions.FOLDERS) {
      return this.prepareSelectedLibrariesForFind().length === 0;
    } else if (findType === FindPrimersOptions.SINGLE) {
      return this.get('searchExisting')!.findWhere({selected: true}) == null;
    } else if (findType === FindPrimersOptions.BASES) {
      return false;
    } else if (findType === FindPrimersOptions.WORKLIST) {
      return this.prepareSelectedLibrariesForFind().length === 0;
    } else if (findType === FindPrimersOptions.TAG_SCHEMA) {
      return this.get('tagSchemaId') == null;
    } else {
      assertNever(findType);
    }
    return false;
  }

  override close(): void {
    this.removePrimers();
    super.close();
  }

  removePrimers(): void {
    const primers = this.get('sequence').sequencePrimers();
    // TODO(BNCH-73144) We probably shouldn't be looking at cids for this, they all update on refresh
    const newPrimerCids = this.get('results')!.map((primer: SequencePrimer) => primer.cid);
    const newPrimers = primers.filter((primer) => newPrimerCids.includes(primer.get('_primer').cid));
    for (const primer of newPrimers) {
      primer.destroy();
    }
  }

  getNewPrimers(): Array<SequencePrimer> {
    const sequence = this.get('sequence');
    const sequencePrimers = sequence.get('_sequence').sequencePrimers();
    // TODO(BNCH-73144) We probably shouldn't be looking at cids for this, they all update on refresh
    const newPrimerCids = this.get('results')!.map((primer: SequencePrimer) => primer.cid);
    return sequencePrimers.filter((primer) => newPrimerCids.includes(primer.cid));
  }

  prepareSelectedLibrariesForFind(): Array<string> {
    return this.get('selectedLibraries').filter((library) => Boolean(library));
  }

  getMisprimingState(): MisprimeOptions {
    return {
      minMatchingBases: this.get('minMatchingBases'),
      maxMismatches: this.get('maxMismatches'),
      maxTotalMismatches: this.get('maxTotalMismatches'),
      minMeltingTemp: this.get('minMeltingTemp'),
      maxMeltingTemp: this.get('maxMeltingTemp'),
      exactMatch: this.get('exactMatch'),
      uniqueMatch: this.get('uniqueMatch'),
    };
  }
}
